import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  DocumentationVersionRead,
  DocumentationVersionReadStatusEnum,
  SortDirectionEnum,
  useEdmDocumentationVersionReadsQuery,
  CompanyPlanUpgradeFeatureEnum,
} from 'graphql-common';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import CircleLoaderWrapper, {
  CircleLoaderWrapperType,
} from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import FeatureLockState from '@lib/components/FeatureLockState';
import LinkComponent, { LinkSizes, LinkTypes } from '@lib/components/Link/Link';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Trans from '@lib/components/Trans/Trans';
import Typography from '@lib/components/Typography/Typography';
import getFullName from '@lib/utils/getFullName';
import formatReadingDurationHms from '@lib/utils/formatReadingDurationHms';
import { isUpgradePlanRequiredError } from '@lib/utils/graphQLErrors';
import { PER_PAGE_KEY_DEFAULT } from '@lib/enums/urls';
import { PlanState } from '@lib/enums/plan';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import HistoryCard from './HistoryCard/HistoryCard';
import HistoryUpgradeRequiredState from './HistoryUpgradeRequiredState';
import styles from './HistoryViewsTab.module.scss';
import listStyles from './DocumentViewHistory.module.scss';

interface Props {
  documentationId: string;
}

export default function HistoryViewsTab({ documentationId }: Props) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [allItems, setAllItems] = useState<DocumentationVersionRead[]>([]);
  const { getUserAccessByCompany, handleOpenPlanModal }: ContextProps =
    useOutletContext();
  const isPlanLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  const { data, loading, previousData, error } =
    useEdmDocumentationVersionReadsQuery({
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      skip: isPlanLocked,
      variables: {
        filters: [
          { documentationId: { predicate: { eq: documentationId } } },
          {
            status: {
              predicate: {
                eq: DocumentationVersionReadStatusEnum.Read,
              },
            },
          },
        ],
        sorts: { readAt: SortDirectionEnum.Desc },
        page,
        limit: +PER_PAGE_KEY_DEFAULT,
      },
    });

  const isUpgradeRequired = isUpgradePlanRequiredError(error);

  const queryData = data || previousData;
  const newItems = useMemo(
    () =>
      (_get(queryData, 'data.collection', []) ||
        []) as DocumentationVersionRead[],
    [queryData],
  );
  const totalPages = _get(queryData, 'data.metadata.totalPages', 1) as number;
  const firstLoading = loading && !previousData;
  const hasMore = page < totalPages;

  useEffect(() => {
    if (newItems.length && page > 1) {
      setAllItems((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const unique = newItems.filter((item) => !existingIds.has(item.id));
        return [...prev, ...unique];
      });
    } else if (newItems.length && page === 1) {
      setAllItems(newItems);
    }
  }, [newItems, page]);

  if (isPlanLocked) {
    return (
      <FeatureLockState
        title={t('unlock-document-engagement-insights')}
        text={<Trans i18nKey="unlock-document-engagement-insights-text" />}
        onUpgrade={() =>
          handleOpenPlanModal(PlanState.Locked, [
            CompanyPlanUpgradeFeatureEnum.Documentation,
          ])
        }
      />
    );
  }

  if (isUpgradeRequired) {
    return <HistoryUpgradeRequiredState />;
  }

  if (firstLoading) return <CircleLoader />;

  if (!allItems.length && !loading) {
    return (
      <ListEmptyState
        title={t('no-views-recorded')}
        text={t('no-views-recorded-text')}
      />
    );
  }

  return (
    <CircleLoaderWrapper
      isLoading={loading}
      type={CircleLoaderWrapperType.blur}
    >
      <div className={listStyles.list}>
        {allItems.map((read) => {
          const { user, readAt, createdAt, readingDurationSeconds } = read;
          const userName = user ? getFullName(user) : '';
          const avatarUrl =
            _get(user, 'avatarAttached.urls.small') ||
            _get(user, 'avatarAttached.urls.original') ||
            null;

          return (
            <HistoryCard
              key={read.id}
              userName={userName}
              avatarUrl={avatarUrl}
              actionLabel={t('viewed')}
              date={readAt || createdAt}
              indentBody
            >
              <div className={styles.engagementBlock}>
                <Typography
                  variant="caption"
                  className={styles.engagementLabel}
                >
                  {t('engagement-time')}
                </Typography>
                <div className={styles.engagementValue}>
                  <MaterialIcon
                    icon="schedule"
                    symbolsOutlined
                    size="md-16"
                    className={styles.engagementIcon}
                  />
                  <Typography variant="body">
                    {formatReadingDurationHms(readingDurationSeconds)}
                  </Typography>
                </div>
              </div>
            </HistoryCard>
          );
        })}
        {hasMore && (
          <LinkComponent
            size={LinkSizes.small}
            type={LinkTypes.secondaryGray}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('show-more')}
          </LinkComponent>
        )}
      </div>
    </CircleLoaderWrapper>
  );
}
